// imageResolver.js — résolveur d'images : map statique → Wikipedia → fallback compagnie.
//
// Priorité :
//   1. Map statique SHIP_IMAGE_MAP (instantané, pas de réseau)
//   2. Cache localStorage
//   3. Wikipedia lookup
//   4. LINE_IMAGE_MAP — fallback par compagnie si Wikipedia échoue
//   5. null → le composant utilise son propre fallback

import { useEffect, useState } from "react";

const CACHE_KEY        = "croisiere_img_cache_v8";
const POSITIVE_TTL_MS  = 1000 * 60 * 60 * 24 * 30; // 30 jours
const NEGATIVE_TTL_MS  = 1000 * 60 * 60 * 24 * 7;  // 7 jours pour "rien trouvé"
const inflight         = new Map();

// ─── Photos locales navires (/public/ships/) — priorité absolue ──────────────
// Ajouter ici quand un fichier est déposé dans public/ships/
// Nommage : kebab-case.webp (ex: wonder-of-the-seas.webp)
const SHIP_LOCAL_MAP = {
  // RCL
  // "wonder of the seas":       "/ships/wonder-of-the-seas.webp",
  // "symphony of the seas":     "/ships/symphony-of-the-seas.webp",
  // "harmony of the seas":      "/ships/harmony-of-the-seas.webp",
  // "oasis of the seas":        "/ships/oasis-of-the-seas.webp",
  // "allure of the seas":       "/ships/allure-of-the-seas.webp",
  // "navigator of the seas":    "/ships/navigator-of-the-seas.webp",
  // "anthem of the seas":       "/ships/anthem-of-the-seas.webp",
  // "freedom of the seas":      "/ships/freedom-of-the-seas.webp",
  // "independence of the seas": "/ships/independence-of-the-seas.webp",
  // NCL
  // "norwegian escape":         "/ships/norwegian-escape.webp",
  // "norwegian bliss":          "/ships/norwegian-bliss.webp",
  // "norwegian getaway":        "/ships/norwegian-getaway.webp",
  // "norwegian breakaway":      "/ships/norwegian-breakaway.webp",
  // "norwegian encore":         "/ships/norwegian-encore.webp",
  // "norwegian joy":            "/ships/norwegian-joy.webp",
  // "norwegian viva":           "/ships/norwegian-viva.webp",
  // MSC
  // "msc seascape":             "/ships/msc-seascape.webp",
  // "msc seashore":             "/ships/msc-seashore.webp",
  // "msc world america":        "/ships/msc-world-america.webp",
  // "msc meraviglia":           "/ships/msc-meraviglia.webp",
  // "msc divina":               "/ships/msc-divina.webp",
};

// ─── Map statique — ~35 navires populaires ────────────────────────────────────
const SHIP_IMAGE_MAP = {
  // Royal Caribbean
  "icon of the seas":       "https://upload.wikimedia.org/wikipedia/commons/thumb/6/66/Icon_of_the_Seas_Puerto_Rico_2025_%28cropped%29.jpg/3840px-Icon_of_the_Seas_Puerto_Rico_2025_%28cropped%29.jpg",
  "wonder of the seas":     "https://upload.wikimedia.org/wikipedia/commons/thumb/0/09/Wonder_of_the_Seas_Jan_30_2025.jpg/1280px-Wonder_of_the_Seas_Jan_30_2025.jpg",
  "harmony of the seas":    "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b4/RCCL_Harmony_of_the_Seas_%2850991506292%29.jpg/3840px-RCCL_Harmony_of_the_Seas_%2850991506292%29.jpg",
  "symphony of the seas":   "https://upload.wikimedia.org/wikipedia/commons/d/dd/SymphonyOfTheSeas_%28cropped%29_02.jpg",
  "oasis of the seas":      "https://upload.wikimedia.org/wikipedia/commons/8/89/Oasis_of_the_Seas.jpg",
  "allure of the seas":     "https://upload.wikimedia.org/wikipedia/commons/2/24/Allure_of_the_Seas_%28ship%2C_2009%29_001.jpg",
  "anthem of the seas":     "https://upload.wikimedia.org/wikipedia/commons/thumb/1/1b/Anthem_of_the_Seas_Sydney_Departure_2026.jpg/3840px-Anthem_of_the_Seas_Sydney_Departure_2026.jpg",
  "independence of the seas":"https://upload.wikimedia.org/wikipedia/commons/thumb/f/f6/INDEPENDENCE_of_the_SEAS_27-6-2025.jpg/3840px-INDEPENDENCE_of_the_SEAS_27-6-2025.jpg",
  "freedom of the seas":    "https://upload.wikimedia.org/wikipedia/commons/thumb/3/36/Freedom_of_the_Seas_at_Port_Miami_%28March_15%2C_2024%29.jpg/3840px-Freedom_of_the_Seas_at_Port_Miami_%28March_15%2C_2024%29.jpg",
  "navigator of the seas":  "https://upload.wikimedia.org/wikipedia/commons/thumb/1/10/Navigator_of_the_Seas_%28ship%2C_2002%29_in_Ensenada%2C_Mexico_%28August_2024%29_1.jpg/3840px-Navigator_of_the_Seas_%28ship%2C_2002%29_in_Ensenada%2C_Mexico_%28August_2024%29_1.jpg",
  "mariner of the seas":    "https://upload.wikimedia.org/wikipedia/commons/thumb/3/31/Bahamas_Cruise_-_ship_exterior_-_June_2018_%283303%29.jpg/3840px-Bahamas_Cruise_-_ship_exterior_-_June_2018_%283303%29.jpg",
  // Norwegian
  "norwegian aqua":         "https://upload.wikimedia.org/wikipedia/commons/thumb/3/36/Norwegian_Aqua_southampton.JPG/3840px-Norwegian_Aqua_southampton.JPG",
  "norwegian prima":        "https://upload.wikimedia.org/wikipedia/commons/0/0b/Norwegian_Prima_Jan_19_2023.jpg",
  "norwegian bliss":        "https://upload.wikimedia.org/wikipedia/commons/a/ab/Norwegian_Bliss_SE.jpg",
  "norwegian encore":       "https://upload.wikimedia.org/wikipedia/commons/thumb/8/8c/NCLEncoreJuneau.jpg/3840px-NCLEncoreJuneau.jpg",
  "norwegian breakaway":    "https://upload.wikimedia.org/wikipedia/commons/0/06/Norwegian_Breakaway_Jan_20_2023.jpg",
  "norwegian getaway":      "https://upload.wikimedia.org/wikipedia/commons/thumb/7/75/Norwegian_Getaway_Jan_29_2025.jpg/1280px-Norwegian_Getaway_Jan_29_2025.jpg",
  "norwegian epic":         "https://upload.wikimedia.org/wikipedia/commons/thumb/a/a2/NORWEGIAN_EPIC.jpg/3840px-NORWEGIAN_EPIC.jpg",
  // Carnival
  "carnival mardi gras":    "https://upload.wikimedia.org/wikipedia/commons/3/33/Mardi_Gras_ship_22-12-2020_front_view.jpg",
  "carnival celebration":   "https://upload.wikimedia.org/wikipedia/commons/7/75/Carnival_Celebration_%28ship%2C_2022%29_Departs_Miami_%28February_2026%29.jpg",
  "carnival jubilee":       "https://upload.wikimedia.org/wikipedia/commons/thumb/8/86/Carnival_Jubilee_Panorama_Cozumel_2024.jpg/3840px-Carnival_Jubilee_Panorama_Cozumel_2024.jpg",
  "carnival vista":         "https://upload.wikimedia.org/wikipedia/commons/thumb/0/00/2017-03-29_CARNIVAL_VISTA_-_IMO_9692569%2C_in_Willemstad%2C_Cura%C3%A7ao.jpg/3840px-2017-03-29_CARNIVAL_VISTA_-_IMO_9692569%2C_in_Willemstad%2C_Cura%C3%A7ao.jpg",
  "carnival venezia":       "https://upload.wikimedia.org/wikipedia/commons/2/2b/Carnival_Venezia%27s_maiden_arrival_at_Bermuda_2.jpg",
  // MSC
  "msc seashore":           "https://upload.wikimedia.org/wikipedia/commons/thumb/5/57/MSC_Seashore_%28ship%2C_2021%29_Departs_Port_Canaveral_%28February_2026%29.jpg/3840px-MSC_Seashore_%28ship%2C_2021%29_Departs_Port_Canaveral_%28February_2026%29.jpg",
  "msc seascape":           "https://upload.wikimedia.org/wikipedia/commons/thumb/0/09/MSC_Seascape_cruise_ship_2.jpg/3840px-MSC_Seascape_cruise_ship_2.jpg",
  "msc virtuosa":           "https://upload.wikimedia.org/wikipedia/commons/7/72/Msc_virtuosa_2021.JPG",
  "msc grandiosa":          "https://upload.wikimedia.org/wikipedia/commons/thumb/0/0d/MSC_Grandiosa_leaving_Kiel_XII.jpg/3840px-MSC_Grandiosa_leaving_Kiel_XII.jpg",
  // Celebrity
  "celebrity edge":         "https://upload.wikimedia.org/wikipedia/commons/thumb/1/15/CelebrityEDGE_-_Auckland%2C_NZ_-_DSC07116.jpg/3840px-CelebrityEDGE_-_Auckland%2C_NZ_-_DSC07116.jpg",
  "celebrity beyond":       "https://upload.wikimedia.org/wikipedia/commons/b/b2/Celebrity_BEYOND_La_Rochelle.jpg",
  "celebrity ascent":       "https://upload.wikimedia.org/wikipedia/commons/3/36/Celebrity_Ascent_-_Le_Pir%C3%A9e_-_June_2024.jpg",
  // Princess
  "sun princess":           "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c6/Sun_Princess_at_Piraeus_Harbour.jpg/3840px-Sun_Princess_at_Piraeus_Harbour.jpg",
  // Disney
  "disney dream":           "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d1/Disney_Dream_%28ship%2C_2011%29_001.jpg/3840px-Disney_Dream_%28ship%2C_2011%29_001.jpg",
  // Cunard / Holland America
  "queen mary 2":           "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b3/Queen_Mary_2_Boston_July_2015_01_%28cropped%29.jpg/3840px-Queen_Mary_2_Boston_July_2015_01_%28cropped%29.jpg",
  "koningsdam":             "https://upload.wikimedia.org/wikipedia/commons/thumb/2/28/Koningsdam_%2827606053523%29.jpg/3840px-Koningsdam_%2827606053523%29.jpg",
  // Costa
  "costa smeralda":         "https://upload.wikimedia.org/wikipedia/commons/e/e0/Costa_Smeralda_1.jpg",
  // Oceania (luxe) — noms ambigus : « Riviera » seul renvoyait une plage de la Côte
  // d'Azur sur Wikipédia au lieu du navire (2026-06-07).
  "riviera":                "https://upload.wikimedia.org/wikipedia/commons/thumb/6/6c/Riviera_%28ship%2C_2012%29%2C_S%C3%A8te_cf01.jpg/1280px-Riviera_%28ship%2C_2012%29%2C_S%C3%A8te_cf01.jpg",
};

// ─── Fallback par compagnie ───────────────────────────────────────────────────
const LINE_IMAGE_MAP = {
  "royal caribbean": "https://upload.wikimedia.org/wikipedia/commons/thumb/4/4a/Icon_of_the_Seas_%28kahunapulej%29.jpg/3840px-Icon_of_the_Seas_%28kahunapulej%29.jpg",
  "norwegian":       "https://upload.wikimedia.org/wikipedia/commons/0/0b/Norwegian_Prima_Jan_19_2023.jpg",
  "carnival":        "https://upload.wikimedia.org/wikipedia/commons/7/75/Carnival_Celebration_%28ship%2C_2022%29_Departs_Miami_%28February_2026%29.jpg",
  "msc":             "https://upload.wikimedia.org/wikipedia/commons/thumb/5/57/MSC_Seashore_%28ship%2C_2021%29_Departs_Port_Canaveral_%28February_2026%29.jpg/3840px-MSC_Seashore_%28ship%2C_2021%29_Departs_Port_Canaveral_%28February_2026%29.jpg",
  "celebrity":       "https://upload.wikimedia.org/wikipedia/commons/3/36/Celebrity_Ascent_-_Le_Pir%C3%A9e_-_June_2024.jpg",
  "princess":        "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c6/Sun_Princess_at_Piraeus_Harbour.jpg/3840px-Sun_Princess_at_Piraeus_Harbour.jpg",
  "disney":          "https://upload.wikimedia.org/wikipedia/commons/thumb/1/17/Disney_Fantasy_at_Castaway_Cay_%2834702733835%29.jpg/3840px-Disney_Fantasy_at_Castaway_Cay_%2834702733835%29.jpg",
  "holland america": "https://upload.wikimedia.org/wikipedia/commons/thumb/2/28/Koningsdam_%2827606053523%29.jpg/3840px-Koningsdam_%2827606053523%29.jpg",
  "costa":           "https://upload.wikimedia.org/wikipedia/commons/e/e0/Costa_Smeralda_1.jpg",
  "cunard":          "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b3/Queen_Mary_2_Boston_July_2015_01_%28cropped%29.jpg/3840px-Queen_Mary_2_Boston_July_2015_01_%28cropped%29.jpg",
};

function detectLine(name) {
  const n = name.toLowerCase().trim();
  if (/\bof the seas\b/.test(n)) return "royal caribbean";
  if (n.startsWith("norwegian ")) return "norwegian";
  if (n.startsWith("carnival ")) return "carnival";
  if (n.startsWith("msc ")) return "msc";
  if (n.startsWith("celebrity ")) return "celebrity";
  if (/\bprincess\b/.test(n)) return "princess";
  if (n.startsWith("disney ")) return "disney";
  if (n.startsWith("queen ") || n.startsWith("cunard")) return "cunard";
  if (["koningsdam","nieuw statendam","nieuw amsterdam","rotterdam","eurodam",
       "oosterdam","westerdam","zuiderdam","volendam","zaandam"].some(s => n.includes(s)))
    return "holland america";
  if (n.startsWith("costa ")) return "costa";
  return null;
}

// ─── Cache ────────────────────────────────────────────────────────────────────
function loadCache() {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY) || "{}"); }
  catch { return {}; }
}
function saveCache(cache) {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(cache)); } catch {}
}
function getCached(key) {
  const entry = loadCache()[key];
  if (!entry) return undefined;
  const age = Date.now() - entry.ts;
  if (entry.url  && age < POSITIVE_TTL_MS) return entry.url;
  if (!entry.url && age < NEGATIVE_TTL_MS) return null;
  return undefined;
}
function setCached(key, url) {
  const cache = loadCache();
  cache[key] = { url, ts: Date.now() };
  saveCache(cache);
}

// ─── Wikipedia lookups ────────────────────────────────────────────────────────
async function fetchSummary(title) {
  const enc = encodeURIComponent(title.replace(/ /g, "_"));
  try {
    const res = await fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${enc}`);
    if (!res.ok) return null;
    const data = await res.json();
    if (data.type === "disambiguation") return null;
    return data.originalimage?.source || data.thumbnail?.source || null;
  } catch { return null; }
}

async function searchTitle(query) {
  try {
    const res = await fetch(
      `https://en.wikipedia.org/w/api.php?action=query&list=search&srsearch=${encodeURIComponent(query)}&srlimit=1&format=json&origin=*`
    );
    if (!res.ok) return null;
    const data = await res.json();
    return data?.query?.search?.[0]?.title || null;
  } catch { return null; }
}

function isUsablePhoto(url) {
  if (!url) return false;
  const u = url.toLowerCase();
  if (u.endsWith(".svg")) return false;
  if (/\bmap\b|_map|map_|carte|flag|coat_of_arms|blason|emblem|logo|locator|location_map|administrative|_relief|topograph|blank|orthograph|region_|_region|in_its_|schéma|diagram|insignia|shield|seal_of/.test(u)) return false;
  return true;
}

async function resolveFromWiki(name, kind) {
  let url = await fetchSummary(name);
  if (url && isUsablePhoto(url)) return url;
  url = null;

  if (kind === "ship") {
    const msName = name.startsWith("MS ") ? null : `MS ${name}`;
    if (msName) {
      url = await fetchSummary(msName);
      if (url && isUsablePhoto(url)) return url;
      url = null;
    }
    const title = await searchTitle(`${name} cruise ship`);
    if (title) {
      url = await fetchSummary(title);
      if (url && isUsablePhoto(url)) return url;
      url = null;
    }
    const t2 = await searchTitle(name);
    if (t2 && t2 !== title) {
      url = await fetchSummary(t2);
      if (url && isUsablePhoto(url)) return url;
    }
    return null;
  }

  const hint = kind === "destination" ? "tourism" : "";
  const title = await searchTitle(hint ? `${name} ${hint}` : name);
  if (title) {
    url = await fetchSummary(title);
    if (url && isUsablePhoto(url)) return url;
    url = null;
  }
  if (hint) {
    const t2 = await searchTitle(name);
    if (t2) {
      url = await fetchSummary(t2);
      if (url && isUsablePhoto(url)) return url;
    }
  }
  return null;
}

function sizedWikiUrl(url, px) {
  if (!url) return url;
  if (url.includes("/thumb/")) return url.replace(/\/(\d+)px-/, `/${px}px-`);
  return url;
}

// ─── API publique ─────────────────────────────────────────────────────────────
export async function resolveImage(name, kind = "ship") {
  if (!name) return null;
  const key = `${kind}:${name.toLowerCase().trim()}`;

  // 1a. Photos locales navires (priorité maximale — instantané, ne tombe jamais en panne)
  if (kind === "ship") {
    const localUrl = SHIP_LOCAL_MAP[name.toLowerCase().trim()];
    if (localUrl) { setCached(key, localUrl); return localUrl; }
  }
  // 1b. Map statique Wikipedia (navires connus)
  if (kind === "ship") {
    const staticUrl = SHIP_IMAGE_MAP[name.toLowerCase().trim()];
    if (staticUrl) {
      setCached(key, staticUrl);
      return staticUrl;
    }
  }

  // 2. Cache localStorage
  const cached = getCached(key);
  if (cached !== undefined) return cached;

  if (inflight.has(key)) return inflight.get(key);
  const p = (async () => {
    try {
      // 3. Wikipedia
      let url = await resolveFromWiki(name, kind);
      // 4. Fallback compagnie si Wikipedia échoue
      if (!url && kind === "ship") {
        const line = detectLine(name);
        if (line) url = LINE_IMAGE_MAP[line] || null;
      }
      setCached(key, url || null);
      return url;
    } finally {
      inflight.delete(key);
    }
  })();
  inflight.set(key, p);
  return p;
}

// Hook React : retourne l'image immédiatement (map statique ou cache), puis
// met à jour si Wikipedia trouve mieux.
export function useWikiImage(name, fallback, kind = "ship") {
  // 1280 = largeur de vignette standard MediaWiki, pré-générée de façon fiable même
  // pour les gros originaux. 900 (non standard) renvoyait 400 sur certains fichiers
  // (Wonder of the Seas, Norwegian Getaway…) → photo navire manquante.
  const targetPx = kind === "ship" ? 1280 : 500;
  const [url, setUrl] = useState(() => {
    if (!name) return fallback;
    const key = `${kind}:${name.toLowerCase().trim()}`;
    // Photos locales — priorité maximale (pas de resize, URL directe)
    if (kind === "ship") {
      const localUrl = SHIP_LOCAL_MAP[name.toLowerCase().trim()];
      if (localUrl) return localUrl;
    }
    // Map statique Wikipedia
    if (kind === "ship") {
      const staticUrl = SHIP_IMAGE_MAP[name.toLowerCase().trim()];
      if (staticUrl) return sizedWikiUrl(staticUrl, targetPx);
    }
    const cached = getCached(key);
    if (cached) return sizedWikiUrl(cached, targetPx);
    return fallback;
  });

  useEffect(() => {
    if (!name) return;
    // Si une photo locale existe, pas besoin de recherche Wikipedia
    if (kind === "ship" && SHIP_LOCAL_MAP[name.toLowerCase().trim()]) return;
    let cancelled = false;
    resolveImage(name, kind).then(resolved => {
      if (!cancelled && resolved) setUrl(sizedWikiUrl(resolved, targetPx));
    });
    return () => { cancelled = true; };
  }, [name, kind, targetPx]);

  return url;
}

export function clearImageCache() {
  try { localStorage.removeItem(CACHE_KEY); } catch {}
}
